import React, { useState } from "react";
import { useLanguage } from "../../../../context/LanguageContext";
import { useAdminSettingsTranslation } from "../translations";
import { DEFAULT_COUNTRIES } from "../../../../utils/currencyUtils";
import { updateMultilingualCountries } from "../../../../firebase/settings-functions";
import "./CountriesTab.css";

// Convert the DEFAULT_COUNTRIES object to an array
const AVAILABLE_COUNTRIES = Object.values(DEFAULT_COUNTRIES);

const CountriesSettings = ({ storeData, setStoreData, setError, storeId }) => {
    const [activeLanguage, setActiveLanguage] = useState('en');
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState("");
    const { language } = useLanguage();
    const { t } = useAdminSettingsTranslation(language);
    // Country selection state
    const [selectedCountry, setSelectedCountry] = useState("");

    // Save countries settings
    const handleSaveCountries = async () => {
        if (!storeId) {
            setError("Store ID is not configured.");
            return;
        }

        setSaving(true);
        setError("");
        setSaveSuccess("");

        try {
            await updateMultilingualCountries(storeId, storeData.availableCountries);
            setSaveSuccess(t('countrySettingsSaved') || "Country settings saved successfully!");

            // Clear success message after 3 seconds
            setTimeout(() => setSaveSuccess(""), 3000);
        } catch (err) {
            setError(err.message || "Failed to save country settings");
        } finally {
            setSaving(false);
        }
    };

    // Add a country to available countries
    const handleAddCountry = () => {
        if (!selectedCountry) return;

        // Check if country already exists
        if (storeData.availableCountries.some(c => c.code === selectedCountry)) {
            setError(t('thisFieldIsRequired')); // Using this as a generic error message
            return;
        }

        const countryToAdd = AVAILABLE_COUNTRIES.find(c => c.code === selectedCountry);

        if (countryToAdd) {
            const newCountry = {
                code: countryToAdd.code,
                multilingual: countryToAdd,
                // Legacy field for backward compatibility
                name: countryToAdd.en.name
            };

            setStoreData({
                ...storeData,
                availableCountries: [...storeData.availableCountries, newCountry]
            });
            setSelectedCountry("");
        }
    };

    // Remove a country from available countries
    const handleRemoveCountry = (code) => {
        setStoreData({
            ...storeData,
            availableCountries: storeData.availableCountries.filter(c => c.code !== code)
        });
    };

    return (
        <div className="countries-settings-section">
            <h2>{t('countrySettings')}</h2>

            {/* Language Switcher */}
            <div className="language-switcher">
                <label>{t('viewCountriesIn')}</label>
                <div className="language-tabs">
                    <button
                        type="button"
                        className={`language-tab ${activeLanguage === 'en' ? 'active' : ''}`}
                        onClick={() => setActiveLanguage('en')}
                    >
                        🇺{t('english')}
                    </button>
                    <button
                        type="button"
                        className={`language-tab ${activeLanguage === 'ar' ? 'active' : ''}`}
                        onClick={() => setActiveLanguage('ar')}
                    >
                        {t('arabic')}
                    </button>
                </div>
            </div>

            <div className="countries-container">
                <p className="section-help">
                    {t('countriesDescription')}
                </p>

                <div className="add-country-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="country-select">{t('addCountry')}</label>
                            <select
                                id="country-select"
                                className="admin-secondary-select"
                                value={selectedCountry}
                                onChange={(e) => setSelectedCountry(e.target.value)}
                            >
                                <option value="">{t('selectCountry')}</option>
                                {AVAILABLE_COUNTRIES.map((country) => (
                                    <option key={country.code} value={country.code}>
                                        {country[activeLanguage]?.name || country.en.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group button-group">
                            <button
                                type="button"
                                className="admin-secondary-btn"
                                onClick={handleAddCountry}
                            >
                                ➕ {t('addCountry')}
                            </button>
                        </div>

                    </div>
                </div>

                {storeData.availableCountries.length > 0 ? (
                    <table className="admin-table countries-table">
                        <thead>
                            <tr>
                                <th>{t('countryCode')}</th>
                                <th>{t('countryName')} ({activeLanguage === 'en' ? t('english') : t('arabic')})</th>
                                <th>{t('actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {storeData.availableCountries.map(country => (
                                <tr key={country.code}>
                                    <td>{country.code}</td>
                                    <td dir={activeLanguage === 'ar' ? 'rtl' : 'ltr'}>
                                        {country.multilingual?.[activeLanguage]?.name ||
                                            country.name ||
                                            country.code}
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="remove-btn"
                                            onClick={() => handleRemoveCountry(country.code)}
                                        >
                                            {t('remove')}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="no-countries">
                        <p>{t('noCountriesAdded')}</p>
                    </div>
                )}
            </div>

            {/* Save Button */}
            <div className="settings-form-actions" style={{ marginTop: '20px' }}>
                {saveSuccess && <div className="admin-success-message">{saveSuccess}</div>}
                <button
                    type="button"
                    className="admin-primary-btn save-settings-btn"
                    onClick={handleSaveCountries}
                    disabled={saving}
                >
                    {saving ? (t('saving') || 'Saving...') : (t('saveCountrySettings') || 'Save Country Settings')}
                </button>
            </div>
        </div>
    );
};

export default CountriesSettings;